import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

// Northstar Analytics Brand Colors
const deepMidnightBlue = [0, 43, 69];
const tealBlue = [31, 181, 165];
const polarisWhite = [249, 251, 255];
const coolGraphite = [74, 74, 74];
const arcticSky = [180, 225, 224];

const addHeader = (doc, subtitle = 'Data Governance Diagnostic Report') => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, 0, pageWidth, 50, 'F');
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.rect(0, 45, pageWidth, 5, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(22);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Northstar Analytics', pageWidth / 2, 20, { align: 'center' });
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'normal');
  doc?.text(subtitle, pageWidth / 2, 31, { align: 'center' });
  doc?.setFontSize(8);
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  doc?.text(`Generated: ${new Date()?.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, pageWidth / 2, 41, { align: 'center' });
};

const addFooter = (doc, pageNumber, totalPages) => {
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.rect(0, pageHeight - 14, pageWidth, 14, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'normal');
  doc?.text('Northstar Analytics | northstaranalytics.eu | Your Guide to Data Excellence', 15, pageHeight - 5);
  doc?.text(`Page ${pageNumber} of ${totalPages}`, pageWidth - 15, pageHeight - 5, { align: 'right' });
};

const checkPageBreak = (doc, yPos, needed = 25) => {
  const pageHeight = doc?.internal?.pageSize?.getHeight();
  if (yPos + needed > pageHeight - 20) {
    doc?.addPage();
    addHeader(doc);
    return 65;
  }
  return yPos;
};

const addSectionTitle = (doc, title, yPos) => {
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(14);
  doc?.setFont('helvetica', 'bold');
  doc?.text(title, 15, yPos);
  doc?.setDrawColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.setLineWidth(1.5);
  doc?.line(15, yPos + 2, 15 + doc?.getTextWidth(title), yPos + 2);
  return yPos + 12;
};

const getMaturityColor = (label) => {
  const map = {
    'Fragmented': [220, 38, 38],
    'Developing': [234, 88, 12],
    'Established': [202, 138, 4],
    'Advanced': [37, 99, 235],
    'Leading': [22, 163, 74],
  };
  return map?.[label] || coolGraphite;
};

const getDomainScoreColor = (score) => {
  if (score >= 4.5) return [22, 163, 74];
  if (score >= 3.5) return [37, 99, 235];
  if (score >= 2.5) return [202, 138, 4];
  if (score >= 1.5) return [234, 88, 12];
  return [220, 38, 38];
};

export const generateDiagnosticPDF = ({ leadInfo, overallScore, overallMaturity, domainScores, insights }) => {
  const doc = new jsPDF();
  const pageWidth = doc?.internal?.pageSize?.getWidth();
  let y = 20;

  // Page 1 Header
  addHeader(doc);
  y = 65;

  // Client Info Banner
  doc?.setFillColor(polarisWhite?.[0], polarisWhite?.[1], polarisWhite?.[2]);
  doc?.setDrawColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setLineWidth(0.5);
  doc?.roundedRect(15, y, pageWidth - 30, 28, 3, 3, 'FD');
  doc?.setTextColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.setFontSize(11);
  doc?.setFont('helvetica', 'bold');
  doc?.text(`Prepared for: ${leadInfo?.fullName}`, 22, y + 9);
  doc?.setFont('helvetica', 'normal');
  doc?.setFontSize(10);
  doc?.text(`Organisation: ${leadInfo?.company}`, 22, y + 18);
  doc?.setFontSize(9);
  doc?.setTextColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.text('CONFIDENTIAL — Northstar Analytics Diagnostic Report', pageWidth - 22, y + 9, { align: 'right' });
  y += 38;

  // Overall Score Section
  y = addSectionTitle(doc, 'Overall Governance Maturity', y);
  const matColor = getMaturityColor(overallMaturity?.label);

  // Score box
  doc?.setFillColor(matColor?.[0], matColor?.[1], matColor?.[2]);
  doc?.roundedRect(15, y, 80, 42, 4, 4, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(36);
  doc?.setFont('helvetica', 'bold');
  doc?.text(overallScore?.toFixed(1), 55, y + 22, { align: 'center' });
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.text('out of 5.0', 55, y + 32, { align: 'center' });

  // Maturity label box
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.roundedRect(100, y, 95, 42, 4, 4, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(18);
  doc?.setFont('helvetica', 'bold');
  doc?.text(overallMaturity?.label, 147, y + 18, { align: 'center' });
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  const descLines = doc?.splitTextToSize(overallMaturity?.description, 85);
  doc?.text(descLines, 147, y + 28, { align: 'center' });
  y += 52;

  // Maturity scale bar
  const scaleItems = [
    { label: 'Fragmented', min: 1.0, max: 1.9, color: [220, 38, 38] },
    { label: 'Developing', min: 2.0, max: 2.9, color: [234, 88, 12] },
    { label: 'Established', min: 3.0, max: 3.9, color: [202, 138, 4] },
    { label: 'Advanced', min: 4.0, max: 4.5, color: [37, 99, 235] },
    { label: 'Leading', min: 4.6, max: 5.0, color: [22, 163, 74] },
  ];
  const barW = (pageWidth - 30) / scaleItems?.length;
  scaleItems?.forEach((item, i) => {
    const isActive = overallScore >= item?.min && overallScore <= item?.max;
    doc?.setFillColor(item?.color?.[0], item?.color?.[1], item?.color?.[2]);
    doc?.setGState(new doc.GState({ opacity: isActive ? 1 : 0.3 }));
    doc?.rect(15 + i * barW, y, barW - 1, 8, 'F');
    doc?.setGState(new doc.GState({ opacity: 1 }));
    doc?.setTextColor(isActive ? item?.color?.[0] : 150, isActive ? item?.color?.[1] : 150, isActive ? item?.color?.[2] : 150);
    doc?.setFontSize(7);
    doc?.setFont('helvetica', isActive ? 'bold' : 'normal');
    doc?.text(item?.label, 15 + i * barW + barW / 2, y + 14, { align: 'center' });
  });
  y += 22;

  // Domain Breakdown
  y = checkPageBreak(doc, y, 80);
  y = addSectionTitle(doc, 'Domain-Level Breakdown', y);

  const domainTableData = domainScores?.map(({ domain, avg }) => {
    const rounded = Math.round(avg);
    const levelLabels = { 1: 'Ad Hoc', 2: 'Emerging', 3: 'Defined', 4: 'Managed', 5: 'Optimised' };
    return [domain?.title, avg?.toFixed(1), levelLabels?.[rounded] || 'Ad Hoc'];
  });

  doc?.autoTable({
    startY: y,
    head: [['Governance Domain', 'Score (1–5)', 'Maturity Level']],
    body: domainTableData,
    theme: 'grid',
    headStyles: {
      fillColor: deepMidnightBlue,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10,
    },
    bodyStyles: { fontSize: 10, textColor: coolGraphite },
    columnStyles: {
      0: { cellWidth: 110 },
      1: { cellWidth: 35, halign: 'center' },
      2: { cellWidth: 45, halign: 'center' },
    },
    alternateRowStyles: { fillColor: polarisWhite },
    margin: { left: 15, right: 15 },
    didParseCell: (data) => {
      if (data?.section === 'body' && data?.column?.index === 1) {
        const score = parseFloat(data?.cell?.raw);
        const c = getDomainScoreColor(score);
        data.cell.styles.textColor = c;
        data.cell.styles.fontStyle = 'bold';
      }
    },
  });

  y = doc?.lastAutoTable?.finalY + 15;

  // Page 2 — Risks, Quick Wins, Roadmap
  doc?.addPage();
  addHeader(doc);
  y = 65;

  // Critical Risks
  y = addSectionTitle(doc, 'Top 3 Critical Risks', y);
  insights?.criticalRisks?.forEach((r, i) => {
    y = checkPageBreak(doc, y, 22);
    doc?.setFillColor(254, 242, 242);
    doc?.setDrawColor(220, 38, 38);
    doc?.setLineWidth(0.3);
    doc?.roundedRect(15, y, pageWidth - 30, 18, 2, 2, 'FD');
    doc?.setFillColor(220, 38, 38);
    doc?.circle(22, y + 9, 4, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${i + 1}`, 22, y + 9 + 3, { align: 'center' });
    doc?.setTextColor(220, 38, 38);
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'bold');
    doc?.text(r?.domain?.toUpperCase(), 30, y + 7);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    const riskLines = doc?.splitTextToSize(r?.risk, pageWidth - 50);
    doc?.text(riskLines?.[0], 30, y + 14);
    y += 22;
  });

  y += 5;

  // Quick Wins
  y = checkPageBreak(doc, y, 80);
  y = addSectionTitle(doc, 'Top 3 Quick Wins', y);
  insights?.quickWins?.forEach((w, i) => {
    y = checkPageBreak(doc, y, 22);
    doc?.setFillColor(240, 253, 244);
    doc?.setDrawColor(22, 163, 74);
    doc?.setLineWidth(0.3);
    doc?.roundedRect(15, y, pageWidth - 30, 18, 2, 2, 'FD');
    doc?.setFillColor(22, 163, 74);
    doc?.circle(22, y + 9, 4, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${i + 1}`, 22, y + 9 + 3, { align: 'center' });
    doc?.setTextColor(22, 163, 74);
    doc?.setFontSize(8);
    doc?.setFont('helvetica', 'bold');
    doc?.text(w?.domain?.toUpperCase(), 30, y + 7);
    doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'normal');
    const winLines = doc?.splitTextToSize(w?.win, pageWidth - 50);
    doc?.text(winLines?.[0], 30, y + 14);
    y += 22;
  });

  y += 5;

  // 90-Day Roadmap
  y = checkPageBreak(doc, y, 60);
  y = addSectionTitle(doc, '90-Day Improvement Roadmap', y);

  const phases = [
    { label: 'Days 1–30', subtitle: 'Foundation', color: [220, 38, 38], bg: [254, 242, 242] },
    { label: 'Days 31–60', subtitle: 'Structure', color: [202, 138, 4], bg: [254, 252, 232] },
    { label: 'Days 61–90', subtitle: 'Embed & Scale', color: [22, 163, 74], bg: [240, 253, 244] },
  ];

  phases?.forEach((phase) => {
    const items = insights?.roadmap?.filter(r => r?.phase === phase?.label);
    if (!items?.length) return;

    y = checkPageBreak(doc, y, 15 + items?.length * 16);

    // Phase header
    doc?.setFillColor(phase?.color?.[0], phase?.color?.[1], phase?.color?.[2]);
    doc?.roundedRect(15, y, pageWidth - 30, 10, 2, 2, 'F');
    doc?.setTextColor(255, 255, 255);
    doc?.setFontSize(9);
    doc?.setFont('helvetica', 'bold');
    doc?.text(`${phase?.label}  ·  ${phase?.subtitle}`, 20, y + 7);
    y += 12;

    items?.forEach((item) => {
      y = checkPageBreak(doc, y, 16);
      doc?.setFillColor(phase?.bg?.[0], phase?.bg?.[1], phase?.bg?.[2]);
      doc?.roundedRect(15, y, pageWidth - 30, 14, 1, 1, 'F');
      doc?.setTextColor(phase?.color?.[0], phase?.color?.[1], phase?.color?.[2]);
      doc?.setFontSize(7);
      doc?.setFont('helvetica', 'bold');
      doc?.text(item?.domain?.toUpperCase(), 22, y + 5);
      doc?.setTextColor(coolGraphite?.[0], coolGraphite?.[1], coolGraphite?.[2]);
      doc?.setFontSize(9);
      doc?.setFont('helvetica', 'normal');
      const actionLines = doc?.splitTextToSize(item?.action, pageWidth - 44);
      doc?.text(actionLines?.[0], 22, y + 11);
      y += 16;
    });
    y += 4;
  });

  // Page 3 — CTA
  doc?.addPage();
  addHeader(doc);
  y = 65;

  // Next Steps CTA
  doc?.setFillColor(deepMidnightBlue?.[0], deepMidnightBlue?.[1], deepMidnightBlue?.[2]);
  doc?.roundedRect(15, y, pageWidth - 30, 70, 5, 5, 'F');
  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.roundedRect(15, y + 65, pageWidth - 30, 5, 0, 0, 'F');

  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(16);
  doc?.setFont('helvetica', 'bold');
  doc?.text('Ready to Act on These Findings?', pageWidth / 2, y + 18, { align: 'center' });
  doc?.setFontSize(10);
  doc?.setFont('helvetica', 'normal');
  doc?.setTextColor(arcticSky?.[0], arcticSky?.[1], arcticSky?.[2]);
  const ctaText = 'This diagnostic is the starting point. Our consultants can facilitate a 1–2 hour stakeholder workshop to validate findings, align priorities, and build your governance transformation roadmap.';
  const ctaLines = doc?.splitTextToSize(ctaText, pageWidth - 50);
  doc?.text(ctaLines, pageWidth / 2, y + 30, { align: 'center' });

  doc?.setFillColor(tealBlue?.[0], tealBlue?.[1], tealBlue?.[2]);
  doc?.roundedRect(pageWidth / 2 - 55, y + 50, 110, 10, 3, 3, 'F');
  doc?.setTextColor(255, 255, 255);
  doc?.setFontSize(9);
  doc?.setFont('helvetica', 'bold');
  doc?.text('northstaranalytics.eu/schedule-consultation', pageWidth / 2, y + 57, { align: 'center' });
  y += 85;

  // Disclaimer
  doc?.setTextColor(150, 150, 150);
  doc?.setFontSize(8);
  doc?.setFont('helvetica', 'italic');
  const disclaimer = 'This report is based on self-assessed responses provided during the diagnostic. Northstar Analytics recommends validating findings through a facilitated stakeholder workshop. All scores and recommendations are indicative and should be reviewed in the context of your organisation\'s specific circumstances.';
  const dLines = doc?.splitTextToSize(disclaimer, pageWidth - 30);
  doc?.text(dLines, pageWidth / 2, y, { align: 'center' });

  // Add footers to all pages
  const totalPages = doc?.internal?.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc?.setPage(i);
    addFooter(doc, i, totalPages);
  }

  const safeName = leadInfo?.company?.replace(/[^a-zA-Z0-9]/g, '-');
  doc?.save(`Northstar-Data-Governance-Diagnostic-${safeName}.pdf`);
};
